import { JTBDOrder, JTBDOrderType } from '../../types/order';
import { ValidNotification } from '../../types/notifications';
import { Order, User } from '../../types/gqlTypes';

interface HasUser {
    user: User;
}

interface CalculateOrderToDisplayParams {
    ordersByEncryptedId: Record<string, Order>;
    selectedOrderUnreadNotification: ValidNotification | undefined;
    deliveredOrder?: Order;
}

interface GetJtbdOrderParams {
    order: Order;
    type: JTBDOrderType;
    isRead: boolean;
    updatedAt: number;
    content?: string;
}

const getJtbdOrder = ({ order, type, isRead, content, updatedAt }: GetJtbdOrderParams): JTBDOrder => ({
    id: order.id,
    status: order.status,
    encryptedId: order.encryptedId,
    seller: {
        profileImageUrl: (order.seller as HasUser).user.profileImageUrl,
        name: (order.seller as HasUser).user.profile.displayName ?? (order.seller as HasUser).user.name,
    },
    type,
    isRead,
    content,
    updatedAt,
});

const getUnreadJtbdOrder = (order: Order, notification: ValidNotification): JTBDOrder =>
    getJtbdOrder({
        order,
        type: 'notification',
        isRead: false,
        content: notification.msg_params.message_content,
        updatedAt: notification.updated_at,
    });

const getDeliveredJtbdOrder = (order: Order, notification: ValidNotification, isRead = true): JTBDOrder =>
    getJtbdOrder({
        order,
        type: 'delivery',
        isRead,
        updatedAt: notification.updated_at,
    });

// Used for better typing.
// We know that notification is defined if selectedOrderUnreadNotification is defined
// and that notificationOrder is defined if notification is defined
const getNotificationOrder = (
    selectedOrderUnreadNotification: ValidNotification | undefined,
    ordersByEncryptedId: Record<string, Order>
): { notification: ValidNotification; notificationOrder: Order } | undefined => {
    if (!selectedOrderUnreadNotification) {
        return;
    }

    return {
        notification: selectedOrderUnreadNotification,
        notificationOrder: ordersByEncryptedId[selectedOrderUnreadNotification.msg_params.order_id],
    };
};

export const selectOrderToDisplay = ({
    ordersByEncryptedId,
    selectedOrderUnreadNotification,
}: CalculateOrderToDisplayParams): JTBDOrder | undefined => {
    const notificationOrderData = getNotificationOrder(selectedOrderUnreadNotification, ordersByEncryptedId);

    if (!notificationOrderData) {
        return;
    }

    const { notificationOrder, notification } = notificationOrderData;

    if (notificationOrderData?.notification?.sub_type === 'delivered') {
        return getDeliveredJtbdOrder(notificationOrder, notification, false);
    }

    if (notificationOrderData?.notificationOrder) {
        return getUnreadJtbdOrder(notificationOrder, notification);
    }
};
